/**
 * Two-line diagnosis cell with paired ICD-10 overflow details.
 */

import { useRef, useState } from "react";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";

/** Raw diagnosis and ICD-10 strings returned by the eligibility API. */
export interface DiagnosisWithOverflowValue {
  diagnosis: string;
  diagnosisCode: string;
}

/** One diagnosis aligned with its ICD-10 code. */
export interface DiagnosisCodePair {
  diagnosis: string;
  code: string;
}

/** Props for the diagnosis overflow table cell. */
interface DiagnosisWithOverflowCellProps {
  value: DiagnosisWithOverflowValue;
}

const DIAGNOSIS_BOUNDARY =
  /,\s+(?=(?:malignant|non[- ]small|invasive|ovarian|breast|lung|prostate|colon|colorectal|pancreatic|renal|hepato|melanoma|leukemia|lymphoma|carcinoma|sarcoma|glioblastoma)\b)/i;

/**
 * Split an API diagnosis string without breaking commas inside descriptions.
 *
 * @param diagnosis - API diagnosis description string
 * @returns Ordered diagnosis descriptions
 */
export function splitDiagnosisDescriptions(diagnosis: string): string[] {
  return diagnosis
    .split(DIAGNOSIS_BOUNDARY)
    .map((description) => description.trim())
    .filter(Boolean);
}

/**
 * Pair comma-separated ICD-10 codes with their diagnosis descriptions.
 *
 * Diagnosis descriptions may themselves contain commas. Clinical diagnosis
 * terms identify true item boundaries; a simple comma split would incorrectly
 * separate qualifiers such as "right breast" or "except renal pelvis".
 *
 * @param diagnosis - API diagnosis description string
 * @param diagnosisCode - API comma-separated ICD-10 string
 * @returns Ordered diagnosis/code pairs
 */
export function pairDiagnosesWithCodes(
  diagnosis: string,
  diagnosisCode: string,
): DiagnosisCodePair[] {
  const codes = diagnosisCode
    .split(",")
    .map((code) => code.trim())
    .filter(Boolean);
  if (codes.length === 0) {
    return diagnosis ? [{ diagnosis: diagnosis.trim(), code: "" }] : [];
  }

  const descriptions = splitDiagnosisDescriptions(diagnosis);
  return codes.map((code, index) => ({
    diagnosis:
      descriptions[index] ||
      (index === 0 ? diagnosis.trim() : "Diagnosis unavailable"),
    code,
  }));
}

/**
 * Render the first diagnosis/code pair and reveal all pairs on hover.
 *
 * @param props - Raw diagnosis and diagnosis-code strings
 * @returns Compact two-line cell plus a portaled details panel
 */
export function DiagnosisWithOverflowCell({
  value,
}: DiagnosisWithOverflowCellProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const pairs = pairDiagnosesWithCodes(
    value?.diagnosis ?? "",
    value?.diagnosisCode ?? "",
  );
  const firstPair = pairs[0];
  const overflowCount = Math.max(0, pairs.length - 1);

  return (
    <div
      ref={anchorRef}
      className="flex h-full w-full flex-col justify-center overflow-hidden"
      onMouseEnter={() => setShowTooltip(pairs.length > 1)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className="flex min-w-0 items-center text-small text-primaryGray-1">
        <span className="truncate">{firstPair?.diagnosis || "—"}</span>
        {overflowCount > 0 && (
          <span className="shrink-0"> +{overflowCount}</span>
        )}
      </div>
      <div className="flex min-w-0 items-center text-tiny text-primaryGray-5">
        <span className="truncate">{firstPair?.code || "—"}</span>
        {overflowCount > 0 && (
          <span className="shrink-0"> +{overflowCount}</span>
        )}
      </div>
      <ToolTipUsingPortal
        placement="bottom-left"
        showTooltip={showTooltip}
        parentRef={anchorRef}
        wrapText
        maxWidth="32.5rem"
      >
        <div className="overflow-hidden rounded-md border border-primaryGray-14 bg-white">
          <div className="grid grid-cols-[1fr_auto] gap-6 border-b border-primaryGray-14 bg-primaryGray-16 px-4 py-2 text-small font-semibold uppercase text-primaryGray-5">
            <span>Diagnosis</span>
            <span>ICD-10</span>
          </div>
          {pairs.map((pair, index) => (
            <div
              key={`${pair.code}-${index}`}
              className="grid grid-cols-[1fr_auto] gap-6 border-b border-primaryGray-14 px-4 py-2 text-small text-primaryGray-2 last:border-b-0"
            >
              <span>{pair.diagnosis}</span>
              <span className="whitespace-nowrap">{pair.code || "—"}</span>
            </div>
          ))}
        </div>
      </ToolTipUsingPortal>
    </div>
  );
}

export default DiagnosisWithOverflowCell;
